import { useState, useEffect } from 'react';
import { X, Banknote, CreditCard, Send, Check, Plus, Trash2, DollarSign, ReceiptText, Loader2, Search } from 'lucide-react';
import { creditNotesService } from '@/services/credit-notes.service';
import { useAuthStore } from '@/stores/auth-store';

export interface PaymentEntry {
  id: string;
  method: 'CASH' | 'CARD' | 'TRANSFER' | 'CREDIT' | 'NOTA_CREDITO';
  amount: number;
  reference: string;
}

interface CreditNoteInfo {
  id: string;
  remaining: number;
  originalSale?: { folio: string };
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payments: PaymentEntry[], requestInvoice?: boolean) => void;
  grandTotal: number;
  customerId?: string | null;
}

const METHOD_ICONS: Record<string, React.ReactNode> = {
  CASH: <Banknote size={18} />,
  CARD: <CreditCard size={18} />,
  TRANSFER: <Send size={18} />,
  CREDIT: <Check size={18} />,
  NOTA_CREDITO: <ReceiptText size={18} />,
};

const METHOD_LABELS: Record<string, string> = {
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  TRANSFER: 'Transferencia',
  CREDIT: 'Crédito',
  NOTA_CREDITO: 'Nota de Crédito',
};

const METHOD_COLORS: Record<string, string> = {
  CASH: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  CARD: 'border-blue-300 bg-blue-50 text-blue-700',
  TRANSFER: 'border-violet-300 bg-violet-50 text-violet-700',
  CREDIT: 'border-orange-300 bg-orange-50 text-orange-700',
  NOTA_CREDITO: 'border-teal-300 bg-teal-50 text-teal-700',
};

const METHOD_COLOR_MAP: Record<string, string> = {
  CASH: '#059669',
  CARD: '#2563eb',
  TRANSFER: '#7c3aed',
  CREDIT: '#ea580c',
  NOTA_CREDITO: '#0d9488',
};

let paymentIdCounter = 0;
function newPaymentId() {
  return `pay_${++paymentIdCounter}_${Date.now()}`;
}

export function PaymentModal({ isOpen, onClose, onConfirm, grandTotal, customerId }: PaymentModalProps) {
  const { user } = useAuthStore();
  const [payments, setPayments] = useState<PaymentEntry[]>([
    { id: newPaymentId(), method: 'CASH', amount: grandTotal, reference: '' },
  ]);
  const [creditNotes, setCreditNotes] = useState<CreditNoteInfo[]>([]);
  const [availableCredit, setAvailableCredit] = useState(0);
  const [loadingCredit, setLoadingCredit] = useState(false);
  const [folioInputs, setFolioInputs] = useState<Record<string, string>>({});
  const [requestInvoice, setRequestInvoice] = useState(false);

  const totalAssigned = payments.reduce((sum, p) => sum + (isNaN(p.amount) ? 0 : p.amount), 0);
  const remaining = Math.max(0, grandTotal - totalAssigned);
  const changeAmount = Math.max(0, totalAssigned - grandTotal);
  const isComplete = totalAssigned >= grandTotal;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPayments([{ id: newPaymentId(), method: 'CASH', amount: grandTotal, reference: '' }]);
      setCreditNotes([]);
      setAvailableCredit(0);
      setFolioInputs({});
    }
  }, [isOpen, grandTotal]);

  const handleSearchFolio = async (paymentId: string) => {
    const folio = folioInputs[paymentId]?.trim();
    if (!user?.company?.id || !folio) return;
    setLoadingCredit(true);
    try {
      const note = await creditNotesService.findByFolio(folio, user.company.id);
      setCreditNotes([note]);
      setAvailableCredit(Number(note.remaining));
      
      setPayments(prev => prev.map(p =>
        p.id === paymentId
          ? {
              ...p,
              reference: note.id,
              amount: Math.min(Number(note.remaining), grandTotal),
            }
          : p
      ));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error buscando folio');
      setCreditNotes([]);
      setAvailableCredit(0);
      setPayments(prev => prev.map(p => p.id === paymentId ? { ...p, reference: '', amount: 0 } : p));
    } finally {
      setLoadingCredit(false);
    }
  };

  const updatePayment = (id: string, field: keyof PaymentEntry, value: any) => {
    setPayments(prev => prev.map(p => {
      if (p.id !== id) return p;
      const updated = { ...p, [field]: value };

      // When method changes to NOTA_CREDITO, reset reference (will be auto-set by fetch)
      if (field === 'method' && value === 'NOTA_CREDITO') {
        updated.reference = '';
      }

      return updated;
    }));
  };

  const addPayment = () => {
    if (remaining <= 0) return;
    setPayments(prev => [...prev, {
      id: newPaymentId(),
      method: 'CASH',
      amount: remaining,
      reference: '',
    }]);
  };

  const removePayment = (id: string) => {
    if (payments.length <= 1) return;
    setPayments(prev => prev.filter(p => p.id !== id));
  };

  const handleConfirm = () => {
    if (!isComplete) return;
    onConfirm(payments.map(p => ({
      ...p,
      amount: parseFloat(p.amount.toFixed(2)),
    })), requestInvoice);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div>
            <h2 className="text-xl font-black text-gray-800">Dividir Pago</h2>
            <p className="text-sm text-gray-500 font-medium">Distribuye el total en uno o más métodos</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Total bar */}
          <div className="flex items-center justify-between bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl px-5 py-4 text-white">
            <div>
              <p className="text-xs font-bold uppercase opacity-80 tracking-wider">Total</p>
              <p className="text-3xl font-black">${grandTotal.toFixed(2)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold uppercase opacity-80 tracking-wider">Asignado</p>
              <p className={`text-2xl font-black ${totalAssigned >= grandTotal ? '' : 'opacity-80'}`}>
                ${totalAssigned.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-300 bg-gradient-to-r from-blue-400 to-emerald-400"
              style={{ width: `${Math.min(100, (totalAssigned / grandTotal) * 100)}%` }}
            />
          </div>

          {/* Payment lines */}
          <div className="space-y-3">
            {payments.map((payment, idx) => (
              <div 
                key={payment.id} 
                className={`rounded-xl border-2 p-4 transition-all ${METHOD_COLORS[payment.method].split(' ')[0]} animate-in slide-in-from-right-2`}
                style={idx > 0 ? { animationDuration: '150ms' } : {}}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase opacity-60">Pago #{idx + 1}</span>
                  {payments.length > 1 && (
                    <button
                      onClick={() => removePayment(payment.id)}
                      className="p-1 rounded-md hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {(['CASH', 'CARD', 'TRANSFER', 'CREDIT', 'NOTA_CREDITO'] as const).map(m => (
                    <button
                      key={m}
                      onClick={() => updatePayment(payment.id, 'method', m)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        payment.method === m
                          ? 'bg-white shadow-sm ring-2 ring-current'
                          : 'bg-black/5 hover:bg-black/10 opacity-60 hover:opacity-100'
                      }`}
                      style={payment.method === m ? { color: METHOD_COLOR_MAP[m] } : {}}
                    >
                      {METHOD_ICONS[m]}
                      {METHOD_LABELS[m]}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">$</span>
                    <input
                      type="number"
                      value={payment.amount || ''}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        const capped = payment.method === 'NOTA_CREDITO'
                          ? Math.min(val, availableCredit)
                          : val;
                        updatePayment(payment.id, 'amount', capped);
                      }}
                      className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-bold text-lg bg-white"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  {payment.method !== 'CASH' && payment.method !== 'NOTA_CREDITO' && (
                    <input
                      type="text"
                      value={payment.reference}
                      onChange={(e) => updatePayment(payment.id, 'reference', e.target.value)}
                      className="w-28 px-3 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm bg-white"
                      placeholder="Referencia"
                    />
                  )}
                </div>

                {/* Credit note info */}
                {payment.method === 'NOTA_CREDITO' && (
                  <div className="mt-3">
                    {!payment.reference ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={folioInputs[payment.id] || ''}
                          onChange={(e) => setFolioInputs(prev => ({ ...prev, [payment.id]: e.target.value.toUpperCase() }))}
                          placeholder="Ingresa Folio (ej. NC-A1B2C3)"
                          className="flex-1 px-3 py-2 rounded-lg border border-teal-300 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm font-bold text-teal-800 uppercase"
                        />
                        <button
                          onClick={() => handleSearchFolio(payment.id)}
                          disabled={loadingCredit || !folioInputs[payment.id]}
                          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-lg text-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                          {loadingCredit ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                          Buscar
                        </button>
                      </div>
                    ) : creditNotes.length > 0 ? (
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-2 relative animate-in zoom-in-95 duration-200">
                        <button 
                          onClick={() => {
                            setCreditNotes([]);
                            setAvailableCredit(0);
                            setFolioInputs(prev => ({ ...prev, [payment.id]: '' }));
                            updatePayment(payment.id, 'reference', '');
                            updatePayment(payment.id, 'amount', 0);
                          }}
                          className="absolute top-2 right-2 p-1 text-teal-500 hover:text-teal-700 bg-white hover:bg-teal-100 rounded-md transition-colors"
                        >
                          <X size={14} />
                        </button>
                        <div className="flex items-center justify-between pr-6">
                          <span className="text-[10px] font-bold text-teal-700 uppercase tracking-wider">
                            Saldo disponible
                          </span>
                          <span className="text-lg font-black text-teal-700">
                            ${availableCredit.toFixed(2)}
                          </span>
                        </div>
                        <div className="text-[10px] text-teal-600 space-y-1">
                          <p className="font-bold">Folio: {folioInputs[payment.id]}</p>
                          {payment.amount > 0 && (
                            <p className="font-medium">
                              Monto a aplicar: ${Math.min(payment.amount, availableCredit).toFixed(2)}
                            </p>
                          )}
                        </div>

                        {availableCredit > 0 && (
                          <button
                            onClick={() => {
                              const maxUse = Math.min(grandTotal, availableCredit);
                              updatePayment(payment.id, 'amount', maxUse);
                            }}
                            className="text-[10px] font-bold text-teal-700 bg-teal-100 hover:bg-teal-200 px-2 py-1 rounded transition-colors"
                          >
                            Usar todo el saldo disponible
                          </button>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add payment button */}
          {remaining > 0 && (
            <button
              onClick={addPayment}
              className="w-full py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 font-bold text-sm flex items-center justify-center gap-2 transition-all"
            >
              <Plus size={16} />
              Agregar otro método de pago (${remaining.toFixed(2)} restantes)
            </button>
          )}

          {/* Change amount */}
          {changeAmount > 0 && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign size={18} className="text-emerald-600" />
                <span className="text-sm font-bold text-emerald-700 uppercase">Cambio</span>
              </div>
              <span className="text-xl font-black text-emerald-700">${changeAmount.toFixed(2)}</span>
            </div>
          )}

          {/* Facturación Checkbox */}
          <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-200 flex items-center gap-3 cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => setRequestInvoice(!requestInvoice)}>
            <input type="checkbox" checked={requestInvoice} readOnly className="w-5 h-5 rounded border-blue-400 text-blue-600 focus:ring-blue-500" />
            <div className="flex flex-col">
              <span className="text-sm font-bold text-blue-800">Solicitar Factura</span>
              <span className="text-xs text-blue-600">Se enviará al cliente registrado o público en general.</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={handleConfirm}
            disabled={!isComplete}
            className={`w-full py-4 rounded-xl font-black text-lg uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
              isComplete
                ? 'bg-[#007AFF] text-white hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-500/30'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Check size={20} />
            CONFIRMAR PAGO ({payments.length} método{payments.length !== 1 ? 'es' : ''})
          </button>
        </div>
      </div>
    </div>
  );
}
